export const DEFAULT_API_ERROR_MESSAGE =
  "We are unable to process your request at this time. Please try again shortly. If the issue persists, please contact your system administrator.";

// Extracts the innermost meaningful message from a .NET exception dump.
// .NET formats nested exceptions as: ---> Namespace.ExceptionType: actual message here
const refineDotNetError = (raw) => {
  if (!raw) return null;
  const message = String(raw);

  // Collect all inner exception messages (---> SomeException: <message>)
  const innerRegex = /--->\s*[\w.]+(?:\s*\([^)]*\))?\s*:\s*([\s\S]+?)(?=\s{3,}at\s|\s*---\s*End of inner|$)/gi;
  const innerMatches = [...message.matchAll(innerRegex)];

  if (innerMatches.length > 0) {
    // Deepest (last) inner exception is the most specific root cause
    const deepest = innerMatches[innerMatches.length - 1][1];
    const refined = deepest
      .split("\n")
      .map((l) => l.trim())
      .filter(
        (l) =>
          l &&
          !/^at\s+[\w.]+/.test(l) &&
          !/^clientconnectionid/i.test(l) &&
          !/^error\s*number/i.test(l)
      )
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

    if (refined && refined.length < 500) return refined;
  }

  // Fallback: grab the first exception type's message if it's not generic
  const firstMatch = message.match(
    /[\w.]*(?:exception|error)[\w.]*\s*(?:\([^)]*\))?\s*:\s*(.+?)(?=\s+at\s+[\w.]|\s*--->|$)/i
  );
  if (firstMatch) {
    const refined = firstMatch[1].trim();
    if (
      refined &&
      refined.length < 500 &&
      !/see the inner exception/i.test(refined) &&
      !/an error occurred while/i.test(refined)
    ) {
      return refined;
    }
  }

  return null;
};

export const getFormalApiErrorMessage = (
  apiError,
  fallbackMessage = DEFAULT_API_ERROR_MESSAGE,
  { suppressNotFound = false } = {}
) => {
  const statusCode = apiError?.response?.status;
  const responseData = apiError?.response?.data;
  const rawBody =
    typeof responseData === "string"
      ? responseData
      : JSON.stringify(responseData || "");

  if (statusCode === 404 && suppressNotFound) {
    return undefined;
  }

  if (statusCode >= 500) {
    console.error(
      `[API ${statusCode}]`,
      apiError?.config?.url || apiError?.request?.responseURL || "",
      responseData || apiError?.message
    );
    return refineDotNetError(rawBody) || fallbackMessage;
  }

  const serverMessage =
    typeof responseData === "string"
      ? responseData
      : responseData?.message ||
        responseData?.Message ||
        responseData?.detail ||
        responseData?.title ||
        responseData?.error ||
        responseData?.errors?.[0] ||
        apiError?.message;

  const cleanedMessage = String(serverMessage || "").trim();
  if (!cleanedMessage) return fallbackMessage;

  const refined = refineDotNetError(cleanedMessage);
  if (refined) return refined;

  const technicalPatterns = [
    /network\s*error/i,
    /request\s*failed/i,
    /status\s*code/i,
    /internal\s*server\s*error/i,
    /microsoft\.data/i,
    /at\s+\w+[\.\w]+\s*\(/,
    /error\s*number\s*:/i,
    /an error occurred while fetching data/i,
    /exception/i,
    /stack trace/i,
    /microsoft\./i,
    /entityframework/i,
    /sqlclient/i,
    /sqlexception/i,
    /dbupdateexception/i,
    /system\./i,
    /at\s+[\w.]+\(/i,
    /error\s*number/i,
    /clientconnectionid/i,
    /internal server error/i,
    /network error/i,
    /request failed/i,
    /status code/i,
    /timeout/i,
    /cors/i,
    /string or binary data would be truncated/i,
  ];

  const isTechnical = technicalPatterns.some((p) => p.test(cleanedMessage));
  return isTechnical ? fallbackMessage : cleanedMessage;
};
